import { create } from "zustand";
import type { CrashRound, CrashResult } from "@karrtopelka/game-kit";

/**
 * Minimal Zustand store for crash (one store per app, hard rule). Display state only —
 * the round/result come from useCrash; the rAF climb lives in the Climb component.
 *
 *   idle → climbing → (cashed | crashed)
 *
 * On cash out the climb KEEPS RUNNING to the crash point so the player sees how high it
 * went (cashedMult is the locked number; result lands when the climb finishes revealing).
 */
export type CrashPhase = "idle" | "climbing" | "cashed" | "crashed";

interface CrashState {
  phase: CrashPhase;
  round: CrashRound | null;
  /** The multiplier the player locked when they tapped cash out (null = not cashed). */
  cashedMult: number | null;
  /** The server cashout result, stored as soon as it lands (may arrive before the climb
   *  reaches the crash point). null until the player cashes + the POST resolves. */
  cashoutResult: CrashResult | null;
  result: CrashResult | null;
  /** Best cashout ever (×100), localStorage-backed. */
  best: number;

  begin: (round: CrashRound) => void;
  /** Player tapped cash out — lock the displayed number; the climb keeps running. */
  lockCashout: (multiplier: number) => void;
  /** The server cashout resolved — stash it (revealed when the climb hits the crash). */
  setCashoutResult: (result: CrashResult) => void;
  /** The climb reached the crash point — reveal the cashout result, or a pure crash. */
  revealAtCrash: (crashPoint: number) => void;
  reset: () => void;
}

const BEST_KEY = "kp:crash:best";
function loadBest(): number {
  try {
    return Number.parseInt(localStorage.getItem(BEST_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function saveBest(n: number): void {
  try {
    localStorage.setItem(BEST_KEY, String(n));
  } catch {
    /* blocked — fine */
  }
}

/** Whether revealAtCrash fired before the cashout result arrived — if so, the result is
 *  revealed as soon as it lands. Module-level so the store actions share it. */
let crashReachedPending = false;

function doReveal(
  set: (partial: Partial<CrashState>) => void,
  get: () => CrashState,
  result: CrashResult,
): void {
  if (result.won) {
    if (result.value !== undefined) {
      const nextBest = Math.max(get().best, result.value);
      if (nextBest > get().best) saveBest(nextBest);
      set({ phase: "cashed", result, best: nextBest });
    } else {
      set({ phase: "cashed", result });
    }
  } else {
    set({ phase: "crashed", result });
  }
}

export const useCrashStore = create<CrashState>((set, get) => ({
  phase: "idle",
  round: null,
  cashedMult: null,
  cashoutResult: null,
  result: null,
  best: loadBest(),

  begin: (round) => {
    crashReachedPending = false;
    set({ phase: "climbing", round, cashedMult: null, cashoutResult: null, result: null });
  },

  lockCashout: (multiplier) => set({ cashedMult: multiplier }),

  setCashoutResult: (result) => {
    set({ cashoutResult: result });
    // If the climb already hit the crash and was waiting on this, reveal now.
    if (crashReachedPending) {
      crashReachedPending = false;
      doReveal(set, get, result);
    }
  },

  revealAtCrash: (crashPoint) => {
    const { cashedMult, cashoutResult } = get();
    if (cashoutResult) {
      doReveal(set, get, cashoutResult); // cashout already resolved → reveal the win
    } else if (cashedMult !== null) {
      crashReachedPending = true; // cashed but the POST hasn't landed → wait for it
    } else {
      doReveal(set, get, { won: false, crashPoint }); // never cashed → pure crash
    }
  },

  reset: () => {
    crashReachedPending = false;
    set({
      phase: "idle",
      round: null,
      cashedMult: null,
      cashoutResult: null,
      result: null,
      best: loadBest(),
    });
  },
}));
